import { useMutation, useQueryClient } from '@tanstack/react-query';

import { testHarnessConnection } from '@/lib/api/generated/api';

import { toHarnessError } from '../errors';
import { harnessKeys } from '../queryKeys';
import type { HarnessTestResult } from '../types';

/** What to check, when it is not simply what the harness already has stored. */
export interface TestHarnessVariables {
  id: string;
  /**
   * Candidate values an admin has typed but not saved. Each omitted field falls
   * back to the stored one, so `undefined` here is exactly the old behaviour.
   */
  overrides?: { base_url?: string; token?: string };
}

/**
 * Run a live connection test for one Harness (spec FR-008).
 *
 * The backend updates the harness's `connection_status`/`connection_message`/
 * `connection_checked_at` as a side effect of this call, so the list is
 * invalidated on success to pick up the new badge state — the test result
 * itself (`ok`/`message`) is what the caller surfaces as a toast.
 *
 * That side effect is the reason `overrides` exists and the reason the backend
 * does not persist an overridden result: a form still holding unsaved edits
 * needs to know whether *those* credentials work, but the badge on the list must
 * keep describing the pair the harness will actually run with.
 */
export function useTestHarness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      overrides,
    }: TestHarnessVariables): Promise<HarnessTestResult> => {
      const response = await testHarnessConnection(
        id,
        overrides
          ? { base_url: overrides.base_url, token: overrides.token }
          : undefined,
      );
      if (response.status === 200) return response.data;
      throw toHarnessError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: harnessKeys.all() });
    },
  });
}
